// Production-safe diagnostic ring buffer for the filter-leak bug.
// Persists entries to localStorage so they survive reloads, tab close/reopen,
// and arbitrary user sessions. Silent: no console output here. Inspect via
// window.__dumpFilterDiag(); clear via window.__clearFilterDiag().
//
// Designed to be safe to ship to production:
// - Bounded ring buffer (DIAG_MAX = 50 entries) so it cannot fill localStorage
//   and starve the NgRx persistence meta-reducer.
// - Each call site is responsible for trimming its payload to structural
//   metadata only — do not pass raw NgRx action payloads or user input here.
// - Silent failure: never throws to the calling reducer/component. Every storage
//   access goes through safe-storage, which is where that guarantee now lives.

import { trimRingToBudget } from './diag-ring';
import { readStorageJson, removeStorage, writeStorageJson } from './safe-storage';

const DIAG_KEY = 'filter-leak-diag';
const DIAG_MAX = 50;
/**
 * Character cap on the whole ring. The entry cap bounds the COUNT only, and these entries carry
 * filter trees -- 50 of them is an open-ended amount of localStorage, in the same area the store's
 * persistence blob is fighting for room.
 */
const DIAG_MAX_CHARS = 65536;

/** A stored value that is not an array (stale format, key reused) degrades to an empty ring. */
function readDiag(): any[] {
  const raw = readStorageJson<any[]>('local', DIAG_KEY, []);
  return Array.isArray(raw) ? raw : [];
}

export function logFilterDiag(tag: string, payload?: any): void {
  const arr = readDiag();
  arr.push({ t: Date.now(), tag, payload });
  if (arr.length > DIAG_MAX) {
    arr.splice(0, arr.length - DIAG_MAX);
  }
  writeStorageJson('local', DIAG_KEY, trimRingToBudget(arr, DIAG_MAX_CHARS));
}

if (typeof window !== 'undefined') {
  (window as any)['__dumpFilterDiag'] = () => readDiag();
  (window as any)['__clearFilterDiag'] = () => removeStorage('local', DIAG_KEY);
}
